import React, { useEffect, useMemo, useRef, useState } from 'react';
import cn from "classnames";
import { useSelector } from "react-redux";
import style from "../common.module.scss"
import moment from "moment";
import 'moment/locale/ru'
import "moment/locale/kk";
import "moment/locale/zh-cn";
import DistanceIcon from '../../../../shared/icons/DistanceIcon';
import ArrowDownIcon from '../../../../shared/icons/ArrowDownIcon';
import OrderInfo from '../../../order-info/OrderInfo';
import OrdersSectionItemTableStatus from '../../../section-item/components/OrdersSectionItemTableStatus';
import { numberToFinanceFormat } from '../../../../../utils';
import CustomButton from '../../../../custom/custom-button/CustomButton';
import { useTranslation } from 'react-i18next';
import { CURRENCIES } from '../../../../../mock';
import NotificationIconNew from '../../../../shared/icons/NotificationIconNew';
import NotificationsPopup from '../../../../notification/NotificationsPopup/NotificationsPopup';
import { getCountryIcon } from '../../../../../utils/getCountryIcon';
import { getVehicleTypeName } from "../../../../../utils/getVehicleTypeName";
import OrderInfoMarketplace from '../../../order-info/components/OrderInfoAvto/Marketplace';
import AppTooltip from '../../../../shared/AppTooltip/AppTooltip';
import OrderInfoExpeditor from '../../../order-info-expeditor/OrderInfoExpeditor';
import { UsersIcon } from "lucide-react";
import { isUnloadingActionRequired } from "../utils/isUnloadingActionRequired";
import TenderAssignmentMethodIcon from "../../../../shared/icons/TenderAssignmentMethodIcon";
import AuctionAssignmentMethodIcon from "../../../../shared/icons/AuctionAssignmentMethodIcon";

const ASSIGNMENT_METHOD_ICONS = {
  Tender: {
    Icon: TenderAssignmentMethodIcon,
    labelKey: "ordersAvailablePage.tenderInProgress",
  },
  Auction: {
    Icon: AuctionAssignmentMethodIcon,
    labelKey: "ordersAvailablePage.auctionInProgress",
  },
};

const OrdersTable = ({
  items = [],
  showTable = true,
  isExpeditor = false,
  isRailway = false,
  onOpenOrder,
  companyName,
  onForceUpdate,
  isShowOrderSolution,
  isSearchPage = false,
  notificationsMap = {},
  setNotificationsMap,
  isAvailablePage,
  isMarketplacePage = false,
  isOwn = false,
  selectedIds = [],
  setSelectedIds,
}) => {
  const user = useSelector((state) => state.user);
  const tableRef = useRef(null);
  const { t, i18n } = useTranslation();
  const notificationRefs = useRef([]);

  const [isOpenInfo, setIsOpenInfo] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [orderInfo, setOrderInfo] = useState(null);
  const [openNotificationOrderId, setOpenNotificationOrderId] = useState(null);

  const handleToggleNotification = (orderId) => {
    setOpenNotificationOrderId((prevId) => (prevId === orderId ? null : orderId));
  };

  const handleToggleCheckbox = (itemId) => {
    setSelectedIds((prev) => {
      if (prev.includes(itemId)) {
        return prev.filter((id) => id !== itemId);
      } else {
        return [...prev, itemId];
      }
    });
  };

  const handleToggleSelectAll = () => {
    if (selectedIds.length === items.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(items.map((item) => item.id));
    }
  };

  const handleOpenOrderInfo = (orderInfo, orderId) => {
    if (onOpenOrder) {
      onOpenOrder(orderId);
      return;
    }
    setSelectedOrderId(null)
    setSelectedOrderId(orderId);
    setOrderInfo(orderInfo);
    setIsOpenInfo(true);
  };

  const handleClickOutside = (event) => {
    if (event.target.closest("[data-portal-modal]")) return;
    if (tableRef.current && !tableRef.current.contains(event.target)) {
      setIsOpenInfo(false);
    }
  };

  useEffect(() => {
    if (isOpenInfo) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpenInfo]);

  useEffect(() => {
    const langAliasMap = {
      ru: "ru",
      kz: "kk",
      cn: "zh-cn",
      en: "en",
    };

    const onLanguageChanged = (lng) => {
      const momentLocale = langAliasMap[lng] || "en";
      moment.locale(momentLocale);
    };

    // Подписка на событие смены языка
    i18n.on('languageChanged', onLanguageChanged);

    // Установка при первом рендере
    onLanguageChanged(i18n.language);

    return () => {
      i18n.off('languageChanged', onLanguageChanged);
    };
  }, []);

  const tableHead = useMemo(() => {
    return [
      {
        name: "",
        isSortable: false,
        key: "checkbox",
      },
      {
        name: t("ordersAvailablePage.tableHead.details"),
        isSortable: false,
        key: "number"
      },
      {
        name: t("ordersAvailablePage.tableHead.from"),
        isSortable: false,
        key: "addressFrom",
      },
      {
        component: <DistanceIcon type={2} />,
        isSortable: false,
        key: "addressTo",
      },
      {
        name: t("ordersAvailablePage.tableHead.to"),
        isSortable: false,
        key: "addressTo",
      },
      {
        name: t("ordersAvailablePage.tableHead.status"),
        isSortable: false,
      },
      {
        name: t(isRailway ? "railwayOrder.wagonType" : "ordersAvailablePage.tableHead.transport"),
        isSortable: false,
      },
      {
        name: "",
        isSortable: false,
      }
    ]
  }, [isExpeditor, isRailway, t]);

  return (
    <div className={cn(style.table_wrapper, "table-responsive")} ref={tableRef}>
      {showTable && (
        <table className="table">
        <thead>
          <tr>
            {tableHead.map((item, idx) => (
              <th scope="col" key={"head-" + idx}>
                {idx === 0 ? (
                  <div className={style.checkboxWrapper} onClick={handleToggleSelectAll}>
                    <input
                      type="checkbox"
                      className={style.checkboxTable}
                      checked={items.length > 0 && selectedIds.length === items.length}
                      onChange={handleToggleSelectAll}
                      readOnly
                    />
                    <div className={style.checkboxCustom}>
                      {items.length > 0 && selectedIds.length === items.length && (
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path
                            d="M11.6666 3.5L5.24992 9.91667L2.33325 7"
                            stroke="white"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className={style.tableHead}>
                    {item.component ? item.component : item.name}
                  </div>
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {items.map((item, idx) => {
            if (!notificationRefs.current[idx]) {
              notificationRefs.current[idx] = React.createRef();
            }
            const unreadCount = (notificationsMap[item.id] || []).filter(n => !n.isRead).length;
            const isNotificationDisabled = Boolean(
              isExpeditor &&
              item?.status?.toLowerCase() !== "awaitingoffer" &&
              item?.expeditorId &&
              user?.companyId &&
              item.expeditorId !== user.companyId,
            );
            const requiresAction = isUnloadingActionRequired(item);
            const assignmentMethodIcon =
              (item.status?.toLowerCase() === "awaitingoffer" && item.isComplexTender === false) ||
              (item.expeditorId && item.expeditorId !== user?.companyId)
                ? ASSIGNMENT_METHOD_ICONS[item.assignmentMethod]
                : null;
            const AssignmentMethodIcon = assignmentMethodIcon?.Icon;

          return (
            <tr
              key={idx}
              className="cursor-pointer"
            >
              <td>
                <div
                  className={style.checkboxWrapper}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleToggleCheckbox(item.id);
                  }}
                >
                  <input
                    type="checkbox"
                    className={style.checkboxTable}
                    checked={selectedIds.includes(item.id)}
                    onChange={(e) => {
                      e.stopPropagation();
                      handleToggleCheckbox(item.id);
                    }}
                    readOnly
                  />
                  <div className={style.checkboxCustom}>
                    {selectedIds.includes(item.id) && (
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                          d="M11.6666 3.5L5.24992 9.91667L2.33325 7"
                          stroke="white"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    )}
                  </div>
                </div>
              </td>
              <td onClick={() => handleOpenOrderInfo(item, item.id)}>
                <div className={style.table_col} style={item?.referenceNumber ? { gap: 0 } : undefined}>
                  {item?.isComplexTender ? (
                    <div className={style.table_col_vehicles}>
                      {item?.vehiclesCount}
                      <UsersIcon size={20} color="#0055ff" />
                    </div>
                  ) : AssignmentMethodIcon ? (
                    <div className={style.table_col_vehicles}>
                      <AssignmentMethodIcon
                        color="#004de6"
                        width="24"
                        height="24"
                        role="img"
                        aria-label={t(assignmentMethodIcon.labelKey)}
                      />
                    </div>
                  ) : (
                    <span>{numberToFinanceFormat(item.price)} {CURRENCIES[item?.currency || "KZT"]?.symbol || ""}</span>
                  )}
                  <span className={style.table_col_summary}>№ {item.number}</span>
                  {item?.referenceNumber && (
                    <>
                      <span
                        className={cn(style.table_col_summary, style.table_col_reference)}
                        data-tooltip-id={`referenceNumber-tooltip-${item.id}`}
                      >
                        №{item?.referenceNumber}
                      </span>
                      <AppTooltip id={`referenceNumber-tooltip-${item.id}`} delayShow={50}>
                        №{item?.referenceNumber}
                      </AppTooltip>
                    </>
                  )}
                </div>
              </td>
              <td onClick={() => handleOpenOrderInfo(item, item.id)}>
                <div className={style.table_col}>
                  <div className={style.table_col_country}>
                    <img
                      src={getCountryIcon(item.countryIdFrom)}
                      className={style.table_col_countryIcon}
                    />
                    {item.cityFromName}
                  </div>
                  <span className={style.table_col_summary}>
                    {moment
                      .utc(item.shippingDate)
                      .startOf("day")
                      .add(item.shippingHourFrom, "hours")
                      .format("D MMMM, HH:mm")}
                  </span>
                </div>
              </td>
              <td onClick={() => handleOpenOrderInfo(item, item.id)}>
                {/* <div className={style.table_col_distance}>
                  {numberToFinanceFormat(item.distance || "-")} {t("analytics.distance_metric")}
                </div> */}
              </td>
              <td onClick={() => handleOpenOrderInfo(item, item.id)}>
                <div className={style.table_col}>
                  <div className={style.table_col_country}>
                    <img
                      src={getCountryIcon(item.countryIdTo)}
                      className={style.table_col_countryIcon}
                    />
                    {item.cityToName}
                  </div>
                  <span className={style.table_col_summary}>
                    {item?.unloadingDate
                      ? moment
                          .utc(item.unloadingDate)
                          .startOf("day")
                          .add(item.unloadingHourFrom, "hours")
                          .format("D MMMM, HH:mm")
                      : t("orderInfo.ordersInfoDirections.awaitingDateAssignment")}
                  </span>
                </div>
              </td>
              <td onClick={() => handleOpenOrderInfo(item, item.id)}>
                <div>
                  <OrdersSectionItemTableStatus
                    timeSnapshotKey={item}
                    status={item.status}
                    driverName={item.driverName}
                    driverSurname={item.driverSurname}
                    isExpeditor={isExpeditor}
                    expeditorId={item?.expeditorId}
                    assignmentMethod={item?.assignmentMethod}
                    requiresAction={requiresAction}
                  />
                </div>
              </td>
              <td onClick={() => handleOpenOrderInfo(item, item.id)}>
                <div className={style.table_col}>
                  <div className={style.table_col_country}>
                    {isRailway ? item.wagonTypeName || "—" : getVehicleTypeName(item, t)}
                  </div>
                  {!isRailway && <span className={style.table_col_summary}>{item.carModel}</span>}
                </div>
              </td>
              <td style={{cursor: "auto"}}>
                <div className={style.table_col_arrow}>
                  {requiresAction && (
                    <CustomButton
                      text={t("ordersAvailablePage.changeStatus")}
                      isSmall
                      onClick={() => handleOpenOrderInfo(item, item.id)}
                    />
                  )}
                  {isShowOrderSolution && !isOwn && item.status === "awaitingoffer" && (
                    <CustomButton text={t("orderInfo.suggestPrice.suggestButton")} isSmall onClick={() => handleOpenOrderInfo(item, item.id)} />
                  )}
                  {isShowOrderSolution && item.status === "assigned" && (
                    <CustomButton text={t("decisionTitle")} isSmall onClick={() => handleOpenOrderInfo(item, item.id)} />
                  )}
                  {isShowOrderSolution && item.status === "expeditorrejected" && (
                    <CustomButton text={t("mockData.orderStatuses.rejected")} isSmall isWhite onClick={() => handleOpenOrderInfo(item, item.id)} />
                  )}
                  <div
                    className={cn(style.notification, {
                      [style.notification_disabled]: isNotificationDisabled,
                    })}
                    aria-disabled={isNotificationDisabled}
                    ref={notificationRefs.current[idx]}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (isNotificationDisabled) return;
                      handleToggleNotification(item.id);
                    }}
                  >
                    <NotificationIconNew />
                    {!isNotificationDisabled && unreadCount > 0 && (
                      <div className={style.notification_count}>{unreadCount}</div>
                    )}
                  </div>

                  <NotificationsPopup
                    notifications={notificationsMap[item.id] || []}
                    orderData={item}
                    orderType="order"
                    isOpen={!isNotificationDisabled && openNotificationOrderId === item.id}
                    onClose={() => {
                      setOpenNotificationOrderId(null);
                    }}
                    referenceElement={notificationRefs.current[idx]?.current}
                    onNotificationsRead={(readIds) => {
                      setNotificationsMap((prev) => ({
                        ...prev,
                        [item.id]: prev[item.id].map((notif) =>
                          readIds.includes(notif.id)
                            ? { ...notif, isRead: true, readDate: new Date() }
                            : notif
                        ),
                      }));
                    }}
                  />

                  <ArrowDownIcon
                    style={{ transform: "rotate(270deg)" }}
                    onClick={() => handleOpenOrderInfo(item, item.id)}
                    className="cursor-pointer"
                  />
                </div>
              </td>
            </tr>
          )})}
        </tbody>
        </table>
      )}

      {!onOpenOrder && (isExpeditor ? (
        <OrderInfoExpeditor
          isOpen={isOpenInfo}
          setIsOpen={setIsOpenInfo}
          orderId={selectedOrderId}
          companyName={companyName}
          onForceUpdate={onForceUpdate}
        />
      ) : isMarketplacePage ? (
        <OrderInfoMarketplace
          isOpen={isOpenInfo}
          setIsOpen={setIsOpenInfo}
          orderId={selectedOrderId}
          companyName={companyName}
          onForceUpdate={onForceUpdate}
          tenderOrderInfo={orderInfo}
          isShowOrderSolution={isShowOrderSolution}
          isAvailablePage={isAvailablePage}
        />
      ) : (
        <OrderInfo
          isOpen={isOpenInfo}
          setIsOpen={setIsOpenInfo}
          orderId={selectedOrderId}
          companyName={companyName}
          onForceUpdate={onForceUpdate}
          tenderOrderInfo={orderInfo}
          isShowOrderSolution={isShowOrderSolution}
          isAvailablePage={isAvailablePage}
        />
      ))}
    </div>
  );
};

export default OrdersTable;
